import React, { useState, useRef, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useShallow } from 'zustand/react/shallow';
import { Wifi, Router, Server, Camera, Cable, Package, Zap, HardDrive, Monitor, Printer, Laptop, Tv, Gamepad2, Music, Circle, Pencil, ZapOff, Radio, Plug, Sun, Battery } from 'lucide-react';
import { PlacedEquipment, SimpleLayout, EquipmentFace } from '../../types';
import { useProjectStore } from '../../store/projectStore';
import { findLayoutPort, layoutPortKey } from '../../utils/portLayout';
import { makeTemplateForPorts } from '../../utils/layoutTemplates';
import { PortStage } from './PortStage';
import { applyElementCanvasPos, clientToCanvas, notifyEquipmentDrag, readElementCanvasPos } from '../../utils/canvasDrag';
import { portChromeVisibleAtZoom } from '../../utils/canvasCameraPrefs';
import {
  getEquipmentCablePortCount,
  panelOffsetYForTopBadge,
  NAME_BADGE_FALLBACK_WIDTH,
  NAME_BADGE_GLYPH_PX,
  NAMEPLATE_ROW_PX,
} from '../../utils/cableGeometry';
import { buildPortEndpointId, endpointMatchesLayoutPort, parsePortEndpointId } from '../../utils/portNamingSchemes';
import { endpointAtCableCapacity, maxCablesForPortKind, countCablesOnLayoutPort } from '../../utils/portConnectionLimits';
import { requestRestoreFloatingEquipmentPanel } from '../../utils/equipmentPanelUi';
import { useLogicStore } from '../../store/logicStore';
import { isLogicExcluded } from '../../engine/networkLogic';
import { setNodesLogicExcluded, setNodesPoweredOff } from '../../utils/logicParticipation';
interface EquipmentComponentProps {
  equipment: PlacedEquipment;
}

const EquipmentComponent: React.FC<EquipmentComponentProps> = ({ equipment }) => {
  const { 
    setSelectedEquipment, 
    setEquipmentConfigOpen, 
    updateEquipmentPosition,
    updateEquipmentConfig,
    isCableRunMode,
    clearDraggedEquipmentPosition,
    setEquipmentDragActive,
    setSelectedEquipmentForPortView,
    setPortVisualizationOpen,
    setSelectedRack,
    setSelectedCable,
    setSelectedRackEquipment,
    setHoveredEquipmentId,
    setHoveredCableId,
    toggleEquipmentExistingStatus,
    isExistingMode,
    alwaysShowExpandedEquipment,
    portsDisplayMode,
    featureFlags,
    selectedSourceEquipment,
    setSelectedSourceEquipment,
    createCableRun,
    panelVisualization,
    expandBadgesOnHover,
    colourizePorts,
    canvasZoom,
  } = useProjectStore(
    useShallow((s) => ({
      setSelectedEquipment: s.setSelectedEquipment,
      setEquipmentConfigOpen: s.setEquipmentConfigOpen,
      updateEquipmentPosition: s.updateEquipmentPosition,
      updateEquipmentConfig: s.updateEquipmentConfig,
      isCableRunMode: s.isCableRunMode,
      clearDraggedEquipmentPosition: s.clearDraggedEquipmentPosition,
      setEquipmentDragActive: s.setEquipmentDragActive,
      setSelectedEquipmentForPortView: s.setSelectedEquipmentForPortView,
      setPortVisualizationOpen: s.setPortVisualizationOpen,
      setSelectedRack: s.setSelectedRack,
      setSelectedCable: s.setSelectedCable,
      setSelectedRackEquipment: s.setSelectedRackEquipment,
      setHoveredEquipmentId: s.setHoveredEquipmentId,
      setHoveredCableId: s.setHoveredCableId,
      toggleEquipmentExistingStatus: s.toggleEquipmentExistingStatus,
      isExistingMode: s.isExistingMode,
      alwaysShowExpandedEquipment: s.alwaysShowExpandedEquipment,
      portsDisplayMode: s.portsDisplayMode,
      featureFlags: s.featureFlags,
      selectedSourceEquipment: s.selectedSourceEquipment,
      setSelectedSourceEquipment: s.setSelectedSourceEquipment,
      createCableRun: s.createCableRun,
      panelVisualization: s.panelVisualization,
      expandBadgesOnHover: s.expandBadgesOnHover,
      colourizePorts: s.colourizePorts,
      canvasZoom: s.canvasZoom,
    }))
  );
  
  const currentProjectId = useProjectStore(state => state.currentProject?.id);
  const isPanelTarget = useProjectStore(
    (s) => s.selectedEquipmentForPortView?.id === equipment.id && s.isPortVisualizationOpen
  );
  const isHoveredEq = useProjectStore((s) => s.hoveredEquipmentId === equipment.id);
  
  // Subscribe explicitly to hoveredPortIdDuringCableDrag to ensure re-renders when dragging
  const hoveredPortIdDuringCableDrag = useProjectStore(state => state.hoveredPortIdDuringCableDrag);
  const logicOn = useProjectStore((s) => s.getCurrentDesign()?.logicMode === 'logic');
  const deviceLogic = useLogicStore((s) => s.result?.devices[equipment.id]);
  const deviceProblemIssues = useLogicStore(
    useShallow((s) => {
      if (!s.result?.issues?.length) return [];
      return s.result.issues.filter(
        (i) =>
          i.equipmentId === equipment.id &&
          (i.severity === 'error' || i.severity === 'warning')
      );
    })
  );
  const hasDeviceProblem = logicOn && deviceProblemIssues.length > 0;
  const deviceProblemTitle = hasDeviceProblem
    ? deviceProblemIssues
        .slice(0, 3)
        .map((i) => i.message)
        .join('\n') + (deviceProblemIssues.length > 3 ? `\n+${deviceProblemIssues.length - 3} more` : '')
    : '';
  const logicExcluded = isLogicExcluded(equipment.config, equipment.equipment.type);
  const poweredOff = !!equipment.config?.poweredOff;
  const dimUnpowered = !!(
    (logicOn && deviceLogic && !deviceLogic.excluded && deviceLogic.inTopology && !deviceLogic.powered) ||
    poweredOff
  );
  const logicSelectionCount = useLogicStore((s) => s.selectionIds.length);
  
  // Subscribe directly to the portPanelBackground value to ensure re-renders when it changes
  // IMPORTANT: Equipment is design-scoped, so we must read from getCurrentDesign().equipment,
  // not from the legacy currentProject.equipment array
  const portPanelBackground = useProjectStore(state => {
    const design = state.getCurrentDesign();
    const eq = design?.equipment?.find(e => e.id === equipment.id);
    const color = eq?.config?.portPanelBackground || '#e5e7eb';
    return color.startsWith('#') ? color : `#${color}`;
  });
  
  // Subscribe to the entire equipment object to ensure we get all updates
  // IMPORTANT: Read from design-scoped equipment, not legacy currentProject.equipment
  const storeEquipment = useProjectStore(state => {
    const design = state.getCurrentDesign();
    return design?.equipment?.find(e => e.id === equipment.id);
  });
  const badgeAnchor = useProjectStore(state => state.badgeAnchor);
  // Get equipment-specific overrides - read from design-scoped equipment
  const equipmentBadgeAnchor = useProjectStore(state => {
    const design = state.getCurrentDesign();
    const eq = design?.equipment?.find(e => e.id === equipment.id);
    return eq?.config?.badgeAnchor;
  });
  const equipmentAlwaysExpanded = useProjectStore(state => {
    const design = state.getCurrentDesign();
    const eq = design?.equipment?.find(e => e.id === equipment.id);
    return eq?.config?.alwaysExpanded;
  });
  const equipmentShowPortPanel = useProjectStore(state => {
    const design = state.getCurrentDesign();
    const eq = design?.equipment?.find(e => e.id === equipment.id);
    return eq?.config?.showPortPanel;
  });
  
  // Use equipment-specific overrides if set, otherwise use global settings
  const effectiveBadgeAnchor = equipmentBadgeAnchor || badgeAnchor;
  const effectiveAlwaysExpanded = equipmentAlwaysExpanded !== undefined ? equipmentAlwaysExpanded : alwaysShowExpandedEquipment;
  const effectiveShowPortPanel = equipmentShowPortPanel !== undefined ? equipmentShowPortPanel : (portsDisplayMode === 'full');

  const [badgeSize, setBadgeSize] = useState({ w: NAME_BADGE_FALLBACK_WIDTH, h: NAMEPLATE_ROW_PX });
  const badgeWidth = badgeSize.w;
  const badgeHeight = badgeSize.h;

  const [showTooltip, setShowTooltip] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [hasBeenDragged, setHasBeenDragged] = useState(false);
  const [wasExpandedBeforeDrag, setWasExpandedBeforeDrag] = useState(false);
  const [hoveredPortId, setHoveredPortId] = useState<string | null>(null);
  const [logicMenu, setLogicMenu] = useState<{ x: number; y: number } | null>(null);
  const cableDragHoverActiveRef = useRef(false);

  // Endpoint drag hit-tests ports by hiding the handle; that can leave CSS :hover / mouseenter
  // stuck after mouseup. Clear local highlight when the drag hover id is released.
  useEffect(() => {
    const dragging = Boolean(hoveredPortIdDuringCableDrag);
    if (cableDragHoverActiveRef.current && !dragging) {
      setHoveredPortId(null);
    }
    cableDragHoverActiveRef.current = dragging;
  }, [hoveredPortIdDuringCableDrag]);

  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [tempPortPanelColor, setTempPortPanelColor] = useState<string>('#e5e7eb');
  const [isBadgeMounted, setIsBadgeMounted] = useState(false);
  
  // Get the latest equipment data from the store - prioritize storeEquipment over prop
  const latestEquipment = storeEquipment || equipment;
  const deviceName = latestEquipment.equipment.name;
  const customLabel = typeof latestEquipment.config?.label === 'string'
    ? latestEquipment.config.label.trim()
    : '';
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const dragOffsetRef = useRef<{ x: number; y: number }>({
    x: NAME_BADGE_FALLBACK_WIDTH / 2,
    y: NAMEPLATE_ROW_PX / 2,
  });
  const initialEquipmentPositionRef = useRef<{ x: number; y: number } | null>(null);
  const pendingDragPosRef = useRef<{ x: number; y: number } | null>(null);
  const isDraggingRef = useRef(false);
  const layerRectRef = useRef<DOMRect | null>(null);
  const livePosRef = useRef(equipment.position);
  const [currentPosition, setCurrentPosition] = useState(equipment.position);
  const elementRef = useRef<HTMLDivElement>(null);
  const badgeRef = useRef<HTMLDivElement | null>(null);
  const listenersAttachedRef = useRef(false);

  const getIcon = () => {
    const iconProps = { size: NAME_BADGE_GLYPH_PX, strokeWidth: 2.25, style: { display: 'block' as const } };
    switch (equipment.equipment.type) {
      case 'ap':
        return <Wifi {...iconProps} />;
      case 'switch':
        return <Router {...iconProps} />;
      case 'router':
        return <Router {...iconProps} />;
      case 'server':
        return <Server {...iconProps} />;
      case 'camera':
        return <Camera {...iconProps} />;
      case 'cable-run':
        return <Cable {...iconProps} />;
      case 'patch-panel':
        return <Package {...iconProps} />;
      case 'poe-injector':
        return <Zap {...iconProps} />;
      case 'psu':
        return <Zap {...iconProps} />;
      case 'ups':
        return <HardDrive {...iconProps} />;
      case 'wall-outlet':
        return <Plug {...iconProps} />;
      case 'solar-panel':
        return <Sun {...iconProps} />;
      case 'battery':
        return <Battery {...iconProps} />;
      case 'inverter':
        return <Zap {...iconProps} />;
      case 'radio-dish':
      case 'sector-antenna':
        return <Radio {...iconProps} />;
      case 'computer':
        return <Monitor {...iconProps} />;
      case 'printer':
        return <Printer {...iconProps} />;
      case 'laptop':
        return <Laptop {...iconProps} />;
      case 'tv':
        return <Tv {...iconProps} />;
      case 'game-console':
        return <Gamepad2 {...iconProps} />;
      case 'media-player':
        return <Music {...iconProps} />;
      case 'generic-node':
        return <Circle {...iconProps} />;
      default:
        return <Package {...iconProps} />;
    }
  };

  const getIconTint = () => {
    switch (equipment.equipment.type) {
      case 'ap':
        return 'text-yellow-300';
      case 'switch':
        return 'text-blue-300';
      case 'router':
        return 'text-red-300';
      case 'server':
        return 'text-purple-300';
      case 'camera':
        return 'text-green-300';
      case 'cable-run':
        return 'text-gray-300';
      case 'patch-panel':
        return 'text-orange-300';
      case 'poe-injector':
        return 'text-pink-300';
      case 'psu':
        return 'text-lime-300';
      case 'ups':
        return 'text-indigo-300';
      case 'wall-outlet':
        return 'text-stone-300';
      case 'radio-dish':
      case 'sector-antenna':
        return 'text-sky-300';
      case 'computer':
        return 'text-cyan-300';
      case 'printer':
        return 'text-slate-300';
      case 'laptop':
        return 'text-teal-300';
      case 'tv':
        return 'text-emerald-300';
      case 'game-console':
        return 'text-violet-300';
      case 'media-player':
        return 'text-rose-300';
      case 'generic-node':
        return 'text-amber-300';
      case 'solar-panel':
        return 'text-amber-300';
      case 'battery':
        return 'text-emerald-300';
      case 'inverter':
        return 'text-orange-300';
      default:
        return 'text-slate-300';
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    // Don't open sidebar if this was part of a drag operation
    if (hasBeenDragged) {
      setHasBeenDragged(false); // Reset for next interaction
      return;
    }

    // In cable run mode, open port visualization for equipment with multiple ports
    if (isCableRunMode) {
      const portCount = getEquipmentCablePortCount(equipment);
      if (portCount > 1) {
        e.stopPropagation();
        
        // Clear other selections
        setSelectedRack(null);
        setSelectedCable(null);
        setSelectedRackEquipment('', null);
        
        setSelectedEquipmentForPortView(equipment);
        setPortVisualizationOpen(true);
        requestRestoreFloatingEquipmentPanel();
        return;
      }
      // Single port equipment - let canvas handle the connection
      return;
    }
    
    e.stopPropagation(); // Prevent canvas click when clicking equipment (normal mode)

    const logicUi = useLogicStore.getState();
    if (e.shiftKey) {
      logicUi.toggleSelectionId(equipment.id, true);
      logicUi.setFocusDeviceId(equipment.id);
      setSelectedEquipment(equipment);
      return;
    }
    logicUi.setSelectionIds([equipment.id]);
    logicUi.setFocusDeviceId(equipment.id);
    
    // Clear other selections to ensure only this equipment is selected
    setSelectedRack(null);
    setSelectedCable(null);
    setSelectedRackEquipment('', null);

    // Open Configuration modal (port view) instead of right sidebar
    setSelectedEquipment(equipment);
    setSelectedEquipmentForPortView(equipment);
    setPortVisualizationOpen(true);
    requestRestoreFloatingEquipmentPanel();
    setEquipmentConfigOpen(false);
  };

  // Update position when equipment position changes externally
  useEffect(() => {
    if (isDraggingRef.current) return;
    livePosRef.current = equipment.position;
    setCurrentPosition(equipment.position);
  }, [equipment.position]);

  useEffect(() => {
    livePosRef.current = currentPosition;
  }, [currentPosition]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (isCableRunMode && (e.target as HTMLElement).closest('.port')) {
      e.stopPropagation();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    try { window.getSelection()?.removeAllRanges(); } catch {}
    
    const wasExpanded = (showTooltip || effectiveAlwaysExpanded) && !isDraggingRef.current;
    setWasExpandedBeforeDrag(wasExpanded);
    setHasBeenDragged(false);
    try { document.body.style.cursor = 'grabbing'; } catch {}
    
    if (elementRef.current) {
      const layer = elementRef.current.closest('[data-panning-layer="true"]') as HTMLElement | null;
      if (layer) {
        const canvasRect = layer.getBoundingClientRect();
        layerRectRef.current = canvasRect;
        const livePos =
          pendingDragPosRef.current ||
          readElementCanvasPos(elementRef.current) ||
          livePosRef.current;
        const mouse = clientToCanvas(e.clientX, e.clientY, canvasRect, canvasZoom);
        dragOffsetRef.current = { x: mouse.x - livePos.x, y: mouse.y - livePos.y };
        initialEquipmentPositionRef.current = { ...livePos };
        livePosRef.current = livePos;
      }
    }
    
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    attachDocumentListeners();
  }, [showTooltip, effectiveAlwaysExpanded, canvasZoom, isCableRunMode]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    // No-op; we handle movement via document listeners for reliability
  }, []);

  const handleMouseUp = useCallback(() => {
    if (!isDragging) {
      // Simple click: clear capture/cursor just in case
      if (elementRef.current && 'releasePointerCapture' in elementRef.current) {
        try { (elementRef.current as any).releasePointerCapture((elementRef.current as any).pointerId); } catch {}
      }
      try { document.body.style.cursor = ''; } catch {}
      dragStartRef.current = null;
      return;
    }
    
    setIsDragging(false);
    setHasBeenDragged(true);
    
    updateEquipmentPosition(equipment.id, currentPosition);
    clearDraggedEquipmentPosition(equipment.id);
    dragStartRef.current = null;
    
    // Release pointer capture
    if (elementRef.current && 'releasePointerCapture' in elementRef.current) {
      (elementRef.current as any).releasePointerCapture((elementRef.current as any).pointerId);
    }
    // Restore cursor
    try { document.body.style.cursor = ''; } catch {}
  }, [isDragging, currentPosition, equipment.id, updateEquipmentPosition, clearDraggedEquipmentPosition]);


  const handleMouseEnter = (e?: React.MouseEvent) => {
    if (!isDragging) {
      if (expandBadgesOnHover) {
        setShowTooltip(true);
      }
      setIsHovering(true);
      try { setHoveredEquipmentId(equipment.id); } catch {}
    }
  };

  const handleMouseLeave = () => {
    if (isDraggingRef.current) return;
    if (!effectiveAlwaysExpanded) {
      setShowTooltip(false);
    }
    setIsHovering(false);
    try {
      if (isHoveredEq) setHoveredEquipmentId(null);
    } catch {}
  };

  // Get front face layout for mini port visualization
  const frontFaceLayout = useMemo(() => {
    // Check for saved layout first
    if (equipment.config?.layout) {
      // Check for multi-face layout first
      if (equipment.config.layout.multiFace?.faces?.front) {
        return equipment.config.layout.multiFace.faces.front as SimpleLayout;
      }
      
      // Fall back to simple layout
      if (equipment.config.layout.simple) {
        return equipment.config.layout.simple as SimpleLayout;
      }
    }
    
    // Auto-generate layout if none exists
    const portCount = equipment.equipment.specifications?.ports || 8;
    return makeTemplateForPorts(portCount);
  }, [equipment.config?.layout, equipment.equipment.specifications?.ports]);

  // Subscribe to design cables to ensure re-renders when cables change
  const designCables = useProjectStore(state => {
    const design = state.getCurrentDesign();
    return design?.cables || [];
  });

  // Get used port numbers for highlighting (only ports that exist on the front face)
  // IMPORTANT: Cables are design-scoped, so read from getCurrentDesign().cables
  const usedPortIds = useMemo(() => {
    if (!designCables || designCables.length === 0) return new Set<string>();
    
    const used = new Set<string>();
    const ports = frontFaceLayout?.ports || [];
    
    for (const port of ports) {
      const hasCable = designCables.some(
        (c: any) =>
          endpointMatchesLayoutPort(c.from, equipment.id, port, ports) ||
          endpointMatchesLayoutPort(c.to, equipment.id, port, ports)
      );
      if (hasCable) {
        used.add(layoutPortKey(port));
      }
    }
    
    return used;
  }, [equipment.id, frontFaceLayout, designCables]);
  const usedPortIdList = useMemo(() => Array.from(usedPortIds), [usedPortIds]);
  const logicPorts = useLogicStore((s) => s.result?.ports);
  const portCaptions = useMemo(() => {
    if (!logicOn || !logicPorts) return undefined;
    const map: Record<string, string> = {};
    (frontFaceLayout?.ports || []).forEach((p) => {
      const match = Object.values(logicPorts).find(
        (lp) => lp.nodeId === equipment.id && lp.portNumber === p.number && (!lp.kind || lp.kind === String(p.kind))
      );
      if (match?.caption) map[layoutPortKey(p)] = match.caption;
    });
    return Object.keys(map).length ? map : undefined;
  }, [logicOn, logicPorts, frontFaceLayout, equipment.id]);

  // Name-badge size drives port-panel offsets and drag grab point.
  useLayoutEffect(() => {
    const el = badgeRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      if (w > 0 && h > 0) {
        setBadgeSize((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
      }
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [deviceName, customLabel, poweredOff, equipment.isExisting]);

  const detailsVisible = isDragging
    ? wasExpandedBeforeDrag || effectiveAlwaysExpanded
    : effectiveAlwaysExpanded || showTooltip;

  // Attach document listeners on mousedown for reliable drag
  const attachDocumentListeners = useCallback(() => {
    if (listenersAttachedRef.current) return;
    const onMove = (e: MouseEvent) => {
      if (!elementRef.current) return;
      if (!layerRectRef.current) {
        const layer = elementRef.current.closest('[data-panning-layer="true"]') as HTMLElement | null;
        if (!layer) return;
        layerRectRef.current = layer.getBoundingClientRect();
      }
      const canvasRect = layerRectRef.current;
      const mouse = clientToCanvas(e.clientX, e.clientY, canvasRect, canvasZoom);
      let finalPosition = {
        x: mouse.x - dragOffsetRef.current.x,
        y: mouse.y - dragOffsetRef.current.y,
      };
      const { snapToGrid: shouldSnap, snapToGridTarget, gridSize: cell } = useProjectStore.getState();
      if (shouldSnap && (snapToGridTarget === 'icons' || snapToGridTarget === 'both') && cell > 0) {
        finalPosition = {
          x: Math.round(finalPosition.x / cell) * cell,
          y: Math.round(finalPosition.y / cell) * cell,
        };
      }
      if (!isDraggingRef.current && dragStartRef.current) {
        const dx0 = e.clientX - dragStartRef.current.x;
        const dy0 = e.clientY - dragStartRef.current.y;
        if (Math.abs(dx0) > 3 || Math.abs(dy0) > 3) {
          isDraggingRef.current = true;
          setCurrentPosition(finalPosition);
          setIsDragging(true);
          setHoveredCableId(null);
          setEquipmentDragActive(true);
          try { document.body.style.cursor = 'grabbing'; } catch {}
        } else {
          return;
        }
      }
      pendingDragPosRef.current = finalPosition;
      livePosRef.current = finalPosition;
      applyElementCanvasPos(elementRef.current, finalPosition.x, finalPosition.y);
      notifyEquipmentDrag(equipment.id, finalPosition);
    };
    const onUp = () => {
      const lastPos = pendingDragPosRef.current || livePosRef.current;
      if (isDraggingRef.current && lastPos) {
        livePosRef.current = lastPos;
        setCurrentPosition(lastPos);
        setIsDragging(false);
        setHasBeenDragged(true);
        updateEquipmentPosition(equipment.id, lastPos);
        clearDraggedEquipmentPosition(equipment.id);
        notifyEquipmentDrag(equipment.id, null);
      } else {
        notifyEquipmentDrag(equipment.id, null);
      }
      isDraggingRef.current = false;
      layerRectRef.current = null;
      setEquipmentDragActive(false);
      dragStartRef.current = null;
      initialEquipmentPositionRef.current = null;
      setWasExpandedBeforeDrag(false);
      try { document.body.style.cursor = ''; } catch {}
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      listenersAttachedRef.current = false;
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    listenersAttachedRef.current = true;
  }, [canvasZoom, equipment.id, updateEquipmentPosition, clearDraggedEquipmentPosition, setEquipmentDragActive, setHoveredCableId]);

  // Handle port clicks for advanced cable run mode
  const handlePortClick = (portNumber: number, kind?: string) => {
    if (!isCableRunMode) {
      return;
    }

    const portId = buildPortEndpointId(equipment.id, portNumber, { kind });
    
    // If we have a source equipment, create cable
    if (selectedSourceEquipment) {
      const sourcePortId = selectedSourceEquipment.id;
      
      // Check if ports are already in use - read from design-scoped cables
      const design = useProjectStore.getState().getCurrentDesign();
      const designCables = design?.cables || [];
      const sourceOccupied = endpointAtCableCapacity(designCables, sourcePortId);
      const destCount = countCablesOnLayoutPort(
        designCables,
        equipment.id,
        { number: portNumber, kind },
        frontFaceLayout?.ports || []
      );
      if (sourceOccupied || destCount >= maxCablesForPortKind(kind)) {
        alert('One or both ports are already at capacity. Please select different ports.');
        return;
      }
      
      console.log('Creating cable from port:', sourcePortId, 'to port:', portId);
      createCableRun(sourcePortId, portId, 'ethernet', `Port ${portNumber}`, 'Cat6');
      setSelectedSourceEquipment(null);
    } else {
      // No source equipment, set this as the source
      const sourceEquipment = {
        ...equipment,
        id: portId
      };
      setSelectedSourceEquipment(sourceEquipment);
    }
  };

  // Effect to always show expanded if setting is enabled
  useEffect(() => {
    if (effectiveAlwaysExpanded && !isDragging) {
      setShowTooltip(true);
    } else if (!effectiveAlwaysExpanded && !isHovering && !isDragging) {
      setShowTooltip(false);
    }
  }, [effectiveAlwaysExpanded, isDragging, isHovering]);

  // Safety: always restore cursor on global end/cancel/visibility change
  useEffect(() => {
    const restore = () => { try { document.body.style.cursor = ''; } catch {} };
    window.addEventListener('mouseup', restore);
    window.addEventListener('pointerup', restore as any);
    window.addEventListener('blur', restore);
    document.addEventListener('visibilitychange', restore);
    return () => {
      window.removeEventListener('mouseup', restore);
      window.removeEventListener('pointerup', restore as any);
      window.removeEventListener('blur', restore);
      document.removeEventListener('visibilitychange', restore);
    };
  }, []);

  // Reset badge mounted state when component remounts (e.g., after tab switch)
  useEffect(() => {
    setIsBadgeMounted(false);
  }, [currentProjectId]);

  return (
    <div 
      ref={elementRef}
      className="absolute equipment-component select-none" 
        style={{ 
          left: currentPosition.x,
          top: currentPosition.y,
          touchAction: 'none',
          userSelect: 'none',
        }}
        data-equipment-id={equipment.id}
      >
        <div
          ref={(el) => {
            badgeRef.current = el;
            if (el && !isBadgeMounted) {
              setIsBadgeMounted(true);
            }
          }}
          data-equipment-face="true"
          data-equipment-nameplate="true"
          className={`relative select-none equipment-component flex items-stretch ${
            isDragging ? 'cursor-grabbing' : 'cursor-pointer'
          } ${latestEquipment.isExisting || dimUnpowered ? 'opacity-75' : ''} ${
            dimUnpowered ? 'grayscale' : ''
          }`}
          title={`${equipment.equipment.name}${isCableRunMode && (equipment.equipment.specifications?.ports || 1) > 1 ? ' - Click to view ports' : ''}${equipment.isExisting ? ' (Existing)' : ''}`}
          style={{ zIndex: 40, maxWidth: 204 }}
          onClick={handleClick}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseEnter={(e) => handleMouseEnter(e)}
          onMouseLeave={handleMouseLeave}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const logicUi = useLogicStore.getState();
            if (!logicUi.selectionIds.includes(equipment.id)) {
              logicUi.setSelectionIds([equipment.id]);
              logicUi.setFocusDeviceId(equipment.id);
            }
            setLogicMenu({ x: e.clientX, y: e.clientY });
          }}
        >
          {isHoveredEq && (
            <div
              className="pointer-events-none absolute inset-0 z-[50] rounded-md"
              style={{ boxShadow: '0 0 0 2px rgb(192 132 252)' }}
            />
          )}

          {equipment.isExisting && (
            <div className="absolute -top-1.5 -right-1.5 bg-green-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold z-[60]">
              E
            </div>
          )}
          {logicOn && logicExcluded && (
            <div className="absolute -bottom-1 -right-1 bg-slate-700 text-white text-[9px] rounded px-1 z-[60]" title="Excluded from Logic">
              Logic off
            </div>
          )}
          {dimUnpowered && !poweredOff && (
            <div className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center z-[60]" title="Not powered">
              <ZapOff size={10} />
            </div>
          )}

          <button
            type="button"
            className={`relative flex-shrink-0 w-5 self-stretch rounded-l-md border border-r-0 border-black/25 shadow-sm transition-colors ${
              poweredOff
                ? 'bg-red-600 hover:bg-red-500 text-white'
                : 'bg-emerald-500 hover:bg-emerald-400 text-white'
            }`}
            title={poweredOff ? 'Turn on (include in Logic)' : 'Turn off (exclude from Logic)'}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setNodesPoweredOff([equipment.id], !poweredOff);
            }}
          >
            <svg
              viewBox="0 0 16 16"
              width="11"
              height="11"
              aria-hidden
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 overflow-visible"
            >
              <path
                d="M8 2.5v5.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
              <path
                d="M11.6 4.7a4.75 4.75 0 1 1-7.2 0"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            </svg>
          </button>

          <div
            className={`min-w-0 flex-1 overflow-hidden border border-black/25 shadow-sm ${
              hasDeviceProblem ? 'rounded-none border-r-0' : 'rounded-r-md'
            }`}
          >
            <div
              className="flex items-center gap-1.5 px-1.5 py-0.5 bg-slate-800/95 text-white text-[11px] font-semibold min-w-0"
              title={deviceName}
            >
              <span className={`flex-shrink-0 ${getIconTint()}`}>{getIcon()}</span>
              <span className="truncate min-w-0">{deviceName}</span>
            </div>
            {customLabel ? (
              <div
                className="px-1.5 py-0.5 bg-slate-700/95 text-amber-300 text-[10px] font-medium truncate border-t border-white/10"
                title={customLabel}
              >
                {customLabel}
              </div>
            ) : null}
          </div>

          {hasDeviceProblem && (
            <button
              type="button"
              className="relative flex-shrink-0 w-5 self-stretch rounded-r-md border border-black/25 bg-amber-400 text-amber-950 shadow-sm transition-colors hover:bg-amber-300"
              style={{
                boxShadow: '0 0 10px 2px rgba(250, 204, 21, 0.55), inset 0 0 0 1px rgba(255,255,255,0.25)',
              }}
              title={deviceProblemTitle || 'Device has Logic issues'}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                const logicUi = useLogicStore.getState();
                logicUi.setFocusDeviceId(equipment.id);
                logicUi.setSelectionIds([equipment.id]);
                logicUi.setDrawerOpen(true);
                try {
                  setHoveredEquipmentId(equipment.id);
                } catch {
                  /* ignore */
                }
              }}
            >
              <span
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[13px] font-black leading-none animate-pulse"
                aria-hidden
              >
                !
              </span>
            </button>
          )}

          {detailsVisible && (
            <div
              className="absolute left-0 top-full mt-1 z-[45] pointer-events-none rounded-md border border-white/15 bg-slate-900/95 px-2 py-1.5 shadow-lg whitespace-nowrap"
              style={{ minWidth: Math.max(badgeWidth, 120) }}
            >
              <div className="text-[10px] text-slate-300 space-y-0.5">
                {equipment.config?.ssid && (
                  <div><span className="text-slate-500">SSID:</span> {equipment.config.ssid}</div>
                )}
                {equipment.config?.ip && (
                  <div><span className="text-slate-500">IP:</span> {equipment.config.ip}</div>
                )}
                {equipment.config?.vlan && (
                  <div><span className="text-slate-500">VLAN:</span> {equipment.config.vlan}</div>
                )}
                {!equipment.config?.ssid && !equipment.config?.ip && !equipment.config?.vlan && (
                  <div className="italic text-slate-500">Click to configure</div>
                )}
              </div>
            </div>
          )}
        </div>

      {/* Full Port Visualization - Below Badge */}
      {/* In Full Port Display mode, always show ports regardless of badge expansion state */}
      {effectiveShowPortPanel && frontFaceLayout && frontFaceLayout.ports && frontFaceLayout.ports.length > 0 && 
       (isBadgeMounted || badgeRef.current) && (
        <div
          className={`absolute group ${isPanelTarget ? 'port-panel-glow' : ''} ${
            poweredOff ? 'opacity-40 grayscale' : ''
          }`}
          data-port-stage-for={equipment.id}
          style={{
            ...(() => {
              // IMPORTANT: Use fixed 40px reference for port panel positioning to prevent movement when badge expands
              // The 40px represents the collapsed badge size (icon area), which should remain the reference point
              const topPanelY = panelOffsetYForTopBadge(0, Boolean(customLabel));
              
              switch (effectiveBadgeAnchor) {
                case 'center-top':
                  // Panel below badge, centered on the name badge
                  return {
                    left: badgeWidth / 2,
                    top: topPanelY,
                    transform: 'translateX(-50%)',
                  };
                case 'center-bottom':
                  return {
                    left: badgeWidth / 2,
                    top: 0,
                    transform: 'translate(calc(-50%), calc(-100% - 4px))',
                  };
                default:
                  return {
                    left: 0,
                    top: 0,
            transform: (() => {
              switch (effectiveBadgeAnchor) {
                case 'top-left':
                  return `translate(0px, ${topPanelY}px)`;
                case 'top-right':
                  return `translate(calc(-100% + ${badgeWidth}px), ${topPanelY}px)`;
                case 'left':
                  return `translate(${badgeWidth + 4}px, 0px)`;
                case 'right':
                  return `translate(calc(-100% - 4px), 0px)`;
                case 'bottom-left':
                  return `translate(0px, calc(-100% - 4px))`;
                case 'bottom-right':
                  return `translate(calc(-100% + ${badgeWidth}px), calc(-100% - 4px))`;
                default:
                  return `translate(0px, ${topPanelY}px)`;
              }
            })(),
                  };
              }
            })(),
            // Panel sits below cables
            zIndex: 20,
            cursor: isDragging ? 'grabbing' : 'grab',
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        >
          {/* Pencil icon to change background color - only show in shaded mode */}
          {panelVisualization === 'shaded' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                // Initialize temp color with current value when opening modal
                setTempPortPanelColor(portPanelBackground);
                setIsColorPickerOpen(true);
              }}
              className="absolute top-1 right-1 z-10 p-1.5 bg-slate-700 hover:bg-slate-600 text-gray-400 hover:text-white rounded opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
              title="Change port panel background color"
            >
              <Pencil size={14} />
            </button>
          )}
          
          <PortStage
            layout={frontFaceLayout}
            activePortIds={usedPortIdList}
            colourizePorts={colourizePorts}
            matchEditor
            showChrome={portChromeVisibleAtZoom(canvasZoom)}
            hasGlow={isPanelTarget}
            portCaptions={portCaptions}
            onPortClick={
              isCableRunMode
                ? handlePortClick 
                : undefined
            }
            onPortHover={isCableRunMode && !hoveredPortIdDuringCableDrag ? (_num, rect) => {
              setHoveredPortId(rect ? layoutPortKey(rect) : null);
            } : undefined}
            hoveredPortId={(() => {
              if (hoveredPortIdDuringCableDrag) {
                const parsed = parsePortEndpointId(hoveredPortIdDuringCableDrag);
                if (parsed && parsed.equipmentId === equipment.id) {
                  const found = findLayoutPort(frontFaceLayout?.ports, parsed.number, parsed.kind);
                  return found ? layoutPortKey(found) : null;
                }
                return null;
              }
              return hoveredPortId;
            })()}
            style={
              panelVisualization === 'minimalist'
                ? { background: 'transparent', border: '1px solid #64748b' }
                : isColorPickerOpen || (portPanelBackground && portPanelBackground !== '#e5e7eb')
                  ? {
                      background: isColorPickerOpen
                        ? (tempPortPanelColor || portPanelBackground)
                        : portPanelBackground,
                      border: '1px solid #475569',
                    }
                  : { border: '1px solid #475569' }
            }
          />
        </div>
      )}

      {/* Color Picker Modal */}
      {isColorPickerOpen && createPortal((
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50" style={{ zIndex: 10000 }}>
          <div className="bg-slate-800 rounded-lg p-6 w-full max-w-sm shadow-2xl" style={{ zIndex: 10001 }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white">Change Port Panel Color</h2>
              <button
                onClick={() => setIsColorPickerOpen(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Background Color
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={(tempPortPanelColor || portPanelBackground).startsWith('#') ? (tempPortPanelColor || portPanelBackground) : `#${tempPortPanelColor || portPanelBackground}`}
                    onChange={(e) => {
                      e.stopPropagation();
                      const next = (e.target as HTMLInputElement).value; // includes '#'
                      const normalized = next.startsWith('#') ? next : `#${next}`;
                      setTempPortPanelColor(normalized);
                      const currentConfig = storeEquipment?.config || latestEquipment.config || {};
                      updateEquipmentConfig(equipment.id, {
                        ...currentConfig,
                        portPanelBackground: normalized,
                      });
                    }}
                    onInput={(e) => {
                      e.stopPropagation();
                      const next = (e.target as HTMLInputElement).value;
                      const normalized = next.startsWith('#') ? next : `#${next}`;
                      setTempPortPanelColor(normalized);
                      const currentConfig = storeEquipment?.config || latestEquipment.config || {};
                      updateEquipmentConfig(equipment.id, {
                        ...currentConfig,
                        portPanelBackground: normalized,
                      });
                    }}
                    className="w-16 h-10 rounded border border-slate-500 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={tempPortPanelColor || portPanelBackground}
                    onChange={(e) => {
                      e.stopPropagation();
                      const v = e.target.value.trim();
                      const normalized = v === '' ? '#e5e7eb' : (v.startsWith('#') ? v : `#${v}`);
                      setTempPortPanelColor(normalized);
                      const currentConfig = storeEquipment?.config || latestEquipment.config || {};
                      updateEquipmentConfig(equipment.id, {
                        ...currentConfig,
                        portPanelBackground: normalized,
                      });
                    }}
                    className="flex-1 px-3 py-2 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                    placeholder="#e5e7eb"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <button
                  onClick={() => {
                    const def = '#e5e7eb';
                    setTempPortPanelColor(def);
                    const currentConfig = storeEquipment?.config || latestEquipment.config || {};
                    updateEquipmentConfig(equipment.id, {
                      ...currentConfig,
                      portPanelBackground: def,
                    });
                  }}
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded transition-colors"
                >
                  Reset to Default
                </button>
                <div className="flex-1" />
                <button
                  onClick={() => setIsColorPickerOpen(false)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* Cable mode hover border overlay on the name badge */}
      {isCableRunMode && isHovering && (
        <div
          className="absolute pointer-events-none border-2 border-purple-400 animate-pulse rounded-md"
          style={{
            width: badgeWidth,
            height: badgeHeight,
            top: 0,
            left: 0,
            boxSizing: 'border-box',
            zIndex: 55,
          }}
        />
      )}

      {logicMenu && createPortal(
        <div
          className="fixed inset-0 z-[200]"
          onMouseDown={() => setLogicMenu(null)}
        >
          <div
            className="absolute min-w-[180px] rounded-md border border-slate-600 bg-slate-800 text-white text-xs shadow-xl py-1"
            style={{ left: logicMenu.x, top: logicMenu.y }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="w-full text-left px-3 py-1.5 hover:bg-slate-700"
              onClick={() => {
                toggleEquipmentExistingStatus(equipment.id);
                setLogicMenu(null);
              }}
            >
              {equipment.isExisting ? 'Mark as new' : 'Mark as existing'}
            </button>
            <button
              type="button"
              className="w-full text-left px-3 py-1.5 hover:bg-slate-700"
              onClick={() => {
                const ids = useLogicStore.getState().selectionIds;
                const targets = ids.length ? ids : [equipment.id];
                setNodesLogicExcluded(targets, true);
                setLogicMenu(null);
              }}
            >
              Exclude from Logic{logicSelectionCount > 1 ? ` (${logicSelectionCount})` : ''}
            </button>
            <button
              type="button"
              className="w-full text-left px-3 py-1.5 hover:bg-slate-700"
              onClick={() => {
                const ids = useLogicStore.getState().selectionIds;
                const targets = ids.length ? ids : [equipment.id];
                setNodesLogicExcluded(targets, false);
                setLogicMenu(null);
              }}
            >
              Include in Logic
            </button>
            <button
              type="button"
              className="w-full text-left px-3 py-1.5 hover:bg-slate-700"
              onClick={() => {
                useProjectStore.getState().duplicatePlacedEquipment(equipment.id);
                setLogicMenu(null);
              }}
            >
              Duplicate
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default React.memo(EquipmentComponent);